import { useState } from "react";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useGetTaxBenefitsQuery } from "store/apis/estimatedTax";
import { hasUploadedReturnForYear } from "util/hasUploadedReturnForYear";
import {
  BenefitCategory,
  ManualBenefitCategory,
} from "types/Models/estimatedTax";
import { BenefitCategoryRow } from "./BenefitCategoryRow";
import { AddTaxBenefitEntryModal } from "./AddTaxBenefitEntryModal";
import { AddS174BenefitEntryModal } from "./AddS174BenefitEntryModal";
import { UploadTaxReturnModal } from "./UploadTaxReturnModal";
import { MANUAL_CATEGORIES, RND_EXPENSES_CATEGORY } from "./constants";

// TODO: Business assets is temporarily hidden until it's ready to ship.
const HIDDEN_CATEGORIES: BenefitCategory[] = ["BUSINESS_ASSETS"];

type Props = {
  entityId: string;
  year: string;
};

export const TaxBenefits = ({ entityId, year }: Props) => {
  const { data, isLoading, isSuccess, isFetching } = useGetTaxBenefitsQuery({
    entityId,
  });

  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [addEntryCategory, setAddEntryCategory] =
    useState<ManualBenefitCategory | null>(null);
  const [isAddS174Open, setIsAddS174Open] = useState(false);
  const [isUploadReturnOpen, setIsUploadReturnOpen] = useState(false);
  const [bannerDismissedYear, setBannerDismissedYear] = useState<string | null>(
    null
  );

  const returnTaxYear = Number(year) - 1;
  const showEmptyStateBanner =
    bannerDismissedYear !== year &&
    !hasUploadedReturnForYear(data, returnTaxYear);

  const openUploadReturn = () => setIsUploadReturnOpen(true);
  const dismissBanner = () => setBannerDismissedYear(year);

  return (
    <div className="t-flex t-flex-col t-gap-8 t-mt-5 t-pb-20">
      <div>
        <p className="t-m-0 t-text-subtext-lg t-text-text-60">
          Tax benefits from previous years
        </p>
        <p className="t-m-0 t-text-body t-text-neutral-50">
          Deductions and credits aren&apos;t used all at once.
        </p>
        <p className="t-m-0 t-text-body t-text-neutral-50">
          The categories below show the balances you've carried forward and how
          they'll be used.
        </p>
      </div>

      <Async.Root
        isLoading={isLoading || isFetching}
        isSuccess={isSuccess && !isFetching}
        isEmpty={data?.benefits.length === 0}
        renderOnLoad={
          <div className="t-flex t-flex-col t-gap-3">
            <Skeleton className="t-min-h-16" />
            <Skeleton className="t-min-h-16" />
            <Skeleton className="t-min-h-16" />
            <Skeleton className="t-min-h-16" />
          </div>
        }
      >
        <Async.Empty>
          <p className="t-m-0 t-text-body-sm t-text-text-30">
            No tax benefit categories to show yet.
          </p>
        </Async.Empty>
        <Async.Success>
          {data && (
            <>
              {showEmptyStateBanner && (
                <Banner
                  type="warning"
                  icon={
                    <span className="t-flex t-shrink-0 t-self-start t-text-orange-80">
                      <TriangleWarning width="18" height="18" />
                    </span>
                  }
                  title="Upload your latest Form 1120 to automatically populate your tax benefits"
                  description="Upload your latest Form 1120 and we'll import your business losses, R&D deductions, tax overpayments, and asset balances automatically."
                  onClose={dismissBanner}
                  cta={
                    <Button
                      customType="secondary"
                      size="xsmall"
                      onClick={openUploadReturn}
                    >
                      Upload return
                    </Button>
                  }
                />
              )}

              <Accordion.Root
                type="multiple"
                value={expandedCategories}
                onValueChange={setExpandedCategories}
                className="t-flex t-flex-col t-gap-9"
              >
                {data.benefits
                  .filter(
                    (benefit) => !HIDDEN_CATEGORIES.includes(benefit.category)
                  )
                  .map((benefit) => {
                    const manualCategory = MANUAL_CATEGORIES[benefit.category];
                    const isRndExpenses =
                      benefit.category === RND_EXPENSES_CATEGORY;
                    const handleAddNew = () => {
                      if (isRndExpenses) {
                        setIsAddS174Open(true);
                        return;
                      }
                      if (manualCategory) setAddEntryCategory(manualCategory);
                    };
                    return (
                      <BenefitCategoryRow
                        key={benefit.category}
                        benefit={benefit}
                        entityId={entityId}
                        isExpanded={expandedCategories.includes(
                          benefit.category
                        )}
                        onAddNew={
                          manualCategory || isRndExpenses
                            ? handleAddNew
                            : undefined
                        }
                        onUploadReturn={
                          isRndExpenses ? openUploadReturn : undefined
                        }
                      />
                    );
                  })}
              </Accordion.Root>
            </>
          )}
        </Async.Success>
      </Async.Root>

      {addEntryCategory && (
        <AddTaxBenefitEntryModal
          open
          onClose={() => setAddEntryCategory(null)}
          entityId={entityId}
          category={addEntryCategory}
        />
      )}

      {isAddS174Open && (
        <AddS174BenefitEntryModal
          open
          onClose={() => setIsAddS174Open(false)}
          entityId={entityId}
        />
      )}

      {isUploadReturnOpen && (
        <UploadTaxReturnModal
          onClose={() => setIsUploadReturnOpen(false)}
          entityId={entityId}
          defaultTaxYear={returnTaxYear}
        />
      )}
    </div>
  );
};
